import { useCallback, useEffect, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';

import { TariffCards } from 'components/direct-sales/quote/components/TariffCards/TariffCards';
import { TariffTable } from 'components/direct-sales/quote/components/TariffTable/TariffTable';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { stringToBool } from 'utils/misc';
import { RADIO_OPTIONS } from './constants';
import { selectErrors as selectQuoteErrors } from 'state/quote/quote-slice';
import {
  selectIsCompareView,
  setIsCompareView,
  selectProducts,
  selectFilteredProducts,
  selectIsDualRate,
  selectIsLoading
} from 'state/quote/quote-slice';
import { pushEvent } from 'state/analytics/analytics-slice';
import {
  selectIsLoadingPost as selectIsLoadingAbandonedCart
} from 'state/abandoned-cart/abandoned-cart-slice';
import { getRadioOptionLabel } from 'components/direct-sales/quote/utils';
import { Error } from 'components/common/error/Error';
import { getQuoteThunk } from 'state/quote/quote-thunks';
import { useQuotePayload } from 'components/direct-sales/quote/hooks/useQuotePayload';
import { useFmrs } from 'hooks/useFmrs';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAbandonedCartInit } from 'hooks/useAbandonedCart/useAbandonedCartInit';
import {
  postAbandonedCartThunk
} from 'state/abandoned-cart/abandoned-cart-thunks';

export const testId = 'tariffsTestId';

export const Tariffs = () => {
  const dispatch = useAppDispatch();
  const getQuotePayload = useQuotePayload();
  const { isAbandonedCartAvailable, payload: abandonedCartPayload } = useAbandonedCartInit();
  const errors = useSelector(selectQuoteErrors, shallowEqual);
  const allProducts = useSelector(selectProducts, shallowEqual);
  const filteredProducts = useSelector(selectFilteredProducts, shallowEqual);
  const isCompareView = useSelector(selectIsCompareView, shallowEqual);
  const isDualRate = useSelector(selectIsDualRate, shallowEqual);
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const isLoadingAbandonedCart = useSelector(selectIsLoadingAbandonedCart, shallowEqual);
  const hasUpdatedAbandonedCart = useRef(false);

  const { preferredStartDate } = useFmrs();
  const canCompare = allProducts.length > 1;
  const showDisplayType = canCompare && !isLoading && !isLoadingAbandonedCart;
  const displayType = isCompareView && canCompare;

  const abandonedCartEnabled =
    isAbandonedCartAvailable &&
    !isLoadingAbandonedCart

  const getAnalyticsEvent = (boolOptionValue) => {
    return {
      action: 'radio-button-click',
      category: 'nepsales-energy',
      label: getRadioOptionLabel(boolOptionValue, RADIO_OPTIONS)
    };
  };

  const handleChangeDisplayType = (event) => {
    const {
      target: { value }
    } = event;
    const boolValue = stringToBool(value);
    const analyticsEvent = getAnalyticsEvent(boolValue);

    dispatch(setIsCompareView(boolValue));
    dispatch(pushEvent(analyticsEvent));
  };

  const initialiseAbandonedCart = useCallback(async () => {
    await dispatch(postAbandonedCartThunk(abandonedCartPayload));
  }, [abandonedCartPayload, dispatch])

  useEffect(() => {
    const callApi = async () => {
      try {
        const payload = getQuotePayload({
          isEconomy7: isDualRate,
          preferredStartDate
        });
        await dispatch(getQuoteThunk(payload));
        dispatch(
          pushEvent({
            category: 'new quote',
            action: 'shown tariffs',
            label: 'tariffs loaded'
          })
        );
      } catch (e) {
        e && console.error(e);
      }
    };

    callApi();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(!abandonedCartEnabled || hasUpdatedAbandonedCart.current) return;
    hasUpdatedAbandonedCart.current = true

    initialiseAbandonedCart()
      .catch(error => console.error(error));

  }, [abandonedCartEnabled, initialiseAbandonedCart, dispatch]);

  useEffect(() => {
    const analyticsEvent = getAnalyticsEvent(displayType);

    dispatch(setIsCompareView(displayType));
    dispatch(pushEvent(analyticsEvent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayType]);

  return (
    <NsContent data-testid={testId}>
      <Error errors={errors} />
      {showDisplayType && (
        <NsContent>
          <NsRadioInput
            className="inline"
            heading="Display"
            name="compareView"
            onChange={handleChangeDisplayType}
            value={isCompareView}
            options={RADIO_OPTIONS}
          />
        </NsContent>
      )}
      {!isCompareView ? (
        <TariffCards products={filteredProducts} />
      ) : (
        <TariffTable products={filteredProducts} />
      )}
    </NsContent>
  );
};

export default Tariffs;
